import { isEngine } from './manager/plugin-groups.js'
import { OFF_BY_DEFAULT } from './off-by-default.js'

const LOCAL_KEY = 'omni.enabled-plugins.v1'
// every plugin id the person has already seen: one not in it is NEW (2026-10-07)
const KNOWN_KEY = 'omni.known-plugins.v1'
const WEEK = 7 * 24 * 60 * 60 * 1000

export function enabledIds(store) {
  const all = typeof store.all === 'function' ? store.all() : store;
  const ids = [];
  for (const [id, entry] of Object.entries(all)) {
    if (entry.enabled) {
      ids.push(id);
    }
  }
  return ids.sort();
}

export function allIds(store) {
  return Object.keys(store.all()).sort()
}

// Saving only the ON ids switched every new plugin (Flashcards, Quiz me) off for anyone who had used the app before (2026-10-07).
// The ids already seen tell a plugin the person turned off from one that is new.
// First start with this code, nothing seen saved yet: the saved list, plus every plugin installed over a week ago
// (an old one missing from the list was turned off by the person; a recent one was turned off by that bug).
export function knownFrom({ known, list, store, now = Date.now() }) {
  if (Array.isArray(known)) return known
  const old = Object.entries(store.all())
    .filter(([, e]) => !(e.installedAt > now - WEEK))
    .map(([id]) => id)
  return [...new Set([...list, ...old])].sort()
}

export function applyEnabled(store, ids, known = null) {
  const all = store.all();
  const targetSet = new Set(ids);
  const knownSet = known ? new Set(known) : null
  for (const [id, entry] of Object.entries(all)) {
    const isNew = knownSet && !knownSet.has(id) && !targetSet.has(id)
    // a NEW plugin starts on, even when the old bug saved it as off; an OFF_BY_DEFAULT one is left as it is (2026-10-07)
    if (isNew && OFF_BY_DEFAULT.includes(id)) continue
    // engine plugins stay on whatever the saved list says: a newly added one (Plugin helper) was switched off for every existing user (2026-10-07)
    const shouldBeEnabled = isNew || targetSet.has(id) || isEngine(id)
    if (entry.enabled !== shouldBeEnabled) {
      store.setEnabled(id, shouldBeEnabled);
    }
  }
}

export function chooseList({ remote, local }) {
  if (Array.isArray(remote)) {
    return remote;
  }
  if (Array.isArray(local)) {
    return local;
  }
  return null;
}

// on/off of every plugin; main.jsx takes it BEFORE boot, so a switch made while plugins start counts as the person's (2026-10-07)
export function enabledMap(store) {
  return Object.fromEntries(Object.entries(store.all()).map(([id, e]) => [id, Boolean(e.enabled)]))
}

function readJson(storage, key, trace, name) {
  try {
    const raw = storage.getItem(key)
    const value = raw ? JSON.parse(raw) : null
    trace(`read-${name}-ok`, { value })
    return value
  } catch (e) {
    trace(`read-${name}-error`, { error: String(e) })
    return null
  }
}

export async function startUserPlugins(
  store,
  { startEnabled: given, storage, authUrl, fetchFn = fetch, trace = () => {}, isSignedIn = async () => true, now = Date.now() }
) {
  const startEnabled = given ?? enabledMap(store)
  let signedIn = false
  let remote
  let remoteKnown = null

  if (!(await isSignedIn())) {
    trace('fetch-remote-skipped-signed-out', {});
    signedIn = false;
    remote = null;
  } else {
    trace('fetch-remote-start', {});
  try {
    const res = await fetchFn(`${authUrl}/prefs`, { credentials: 'include' });
    if (res.status === 401) {
      trace('fetch-remote-401', {});
      signedIn = false;
      remote = null;
    } else if (res.ok) {
      const data = await res.json();
      signedIn = true;
      remote = data.enabledPlugins;
      remoteKnown = Array.isArray(data.knownPlugins) ? data.knownPlugins : null
      trace('fetch-remote-ok', { remote, remoteKnown });
    } else {
      trace('fetch-remote-error', { status: res.status });
      signedIn = false;
      remote = null;
    }
  } catch (e) {
    trace('fetch-remote-exception', { error: String(e) });
    signedIn = false;
    remote = null;
  }
  }

  trace('read-local-start', {});
  const local = readJson(storage, LOCAL_KEY, trace, 'local')
  const localKnown = readJson(storage, KNOWN_KEY, trace, 'known')

  let list = chooseList({ remote, local });

  if (list === null) {
    list = enabledIds(store);
    trace('list-from-defaults', { list });
  } else {
    // the ids seen come from the same place as the list
    const known = knownFrom({ known: Array.isArray(remote) ? remoteKnown : localKnown, list, store, now })
    // switches the person flipped while the list loaded win over the saved list (2026-10-07)
    const changedWhileLoading = Object.entries(store.all())
      .filter(([id, e]) => id in startEnabled && Boolean(e.enabled) !== startEnabled[id])
      .map(([id, e]) => [id, Boolean(e.enabled)])
    applyEnabled(store, list, known)
    for (const [id, on] of changedWhileLoading) {
      if (Boolean(store.get(id)?.enabled) !== on) store.setEnabled(id, on)
    }
    if (changedWhileLoading.length) {
      trace('kept-changes-made-while-loading', { changed: changedWhileLoading })
    }
    const newIds = allIds(store).filter((id) => !known.includes(id))
    if (newIds.length) trace('new-plugins-kept', { newIds })
    list = enabledIds(store)
    trace('apply-enabled', { list })
  }

  const knownNow = allIds(store)
  trace('save-local-start', { list });
  try {
    storage.setItem(LOCAL_KEY, JSON.stringify(list));
    storage.setItem(KNOWN_KEY, JSON.stringify(knownNow))
    trace('save-local-ok', {});
  } catch (e) {
    trace('save-local-error', { error: String(e) });
  }

  if (signedIn) {
    const same = JSON.stringify(remote ?? null) === JSON.stringify(list) && JSON.stringify(remoteKnown) === JSON.stringify(knownNow)
    if (!same) {
      trace('put-remote-start', { list });
      try {
        await fetchFn(`${authUrl}/prefs`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabledPlugins: list, knownPlugins: knownNow }),
        });
        trace('put-remote-ok', {});
      } catch (e) {
        trace('put-remote-error', { error: String(e) });
      }
    }
  }

  let lastSaved = list;
  store.subscribe(() => {
    const ids = enabledIds(store);
    const idsStr = JSON.stringify(ids);
    const lastSavedStr = JSON.stringify(lastSaved);
    if (idsStr !== lastSavedStr) {
      lastSaved = ids;
      const known = allIds(store)
      trace('subscribe-save-local-start', { ids });
      try {
        storage.setItem(LOCAL_KEY, JSON.stringify(ids));
        storage.setItem(KNOWN_KEY, JSON.stringify(known))
        trace('subscribe-save-local-ok', {});
      } catch (e) {
        trace('subscribe-save-local-error', { error: String(e) });
      }
      if (signedIn) {
        trace('subscribe-put-remote-start', { ids });
        fetchFn(`${authUrl}/prefs`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabledPlugins: ids, knownPlugins: known }),
        }).catch((e) => {
          trace('subscribe-put-remote-error', { error: String(e) });
        });
      }
    }
  });

  trace('start-complete', { signedIn, list });
}
